import { OSId } from '@ps/platform-message-framework/common/OSId';
import type { DrawLevel, ModLevels } from '@ps/web-shared-api-types/v1-preview/SwPkrSpinAndGo';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { lock, unlock } from 'tua-body-scroll-lock';

import { select } from '@swf/core/store';

import getAwardDisplayName from '../../../../utils/getAwardDisplayName';
import HoldemMaxPrizeTable from '../HoldemMaxPrizeTable/HoldemMaxPrizeTable';
import Prize from '../Prize/Prize';
import sharedStyles from '../shared.scss';

import {
  TXTCLI_Coming_soon,
  TXTCLI_These_prize_values_are_average_ELL
} from './PrizePool.messages.json';
import styles from './PrizePool.scss';
import { TableHead } from './TableHead';

interface ExtendedDrawLevel extends DrawLevel {
  awards: (string | number | undefined)[];
}

interface PrizePoolProps {
  currency: number;
  scalePM: number;
  drawLevels: DrawLevel[];
  isHoldemMax: boolean;
  modLevels: ModLevels[];
}

const PrizePool: React.FC<PrizePoolProps> = ({
  currency,
  scalePM,
  drawLevels = [],
  isHoldemMax,
  modLevels
}) => {
  const locale = useSelector(select.locale.get);
  const prizeTableRef = useRef<HTMLDivElement>(null);
  const isIos = useSelector(select.device.getOS) === OSId.I_OS;

  useEffect(() => {
    if (isIos) {
      // for IOS devices it needs to be applied lock/unlock for scrollable element inside the modal
      // https://github.com/tuax/tua-body-scroll-lock?tab=readme-ov-file#targetelement-needs-scrollingios-only
      lock(prizeTableRef.current);
      unlock(prizeTableRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { mappedDrawLevels, placeAwardsMaxLength } = useMemo(() => {
    if (drawLevels.length === 0) {
      return {
        mappedDrawLevels: [],
        placeAwardsMaxLength: 0
      };
    }

    const placeAwardsMaxLength = drawLevels.reduce((acc, curr) => {
      if (curr.placeAwards) {
        acc = curr.placeAwards.length > acc ? curr.placeAwards.length : acc;
      }

      return acc;
    }, 0);

    const mappedDrawLevels = [...drawLevels].map((drawLevel): ExtendedDrawLevel => {
      const awards = Array(placeAwardsMaxLength)
        .fill(undefined)
        .map((_, i) => {
          const awardName = getAwardDisplayName(locale, drawLevel.awardDisplayNames);
          return awardName?.trim() ? awardName : drawLevel.placeAwards && drawLevel.placeAwards[i];
        });

      return {
        ...drawLevel,
        awards
      };
    });

    return {
      mappedDrawLevels,
      placeAwardsMaxLength
    };
  }, [drawLevels, locale]);

  const tableNoContent = (
    <table>
      <tbody className="no-content">
        <tr>
          <td>
            <Translation {...TXTCLI_Coming_soon} />
          </td>
        </tr>
      </tbody>
    </table>
  );

  if (mappedDrawLevels.length === 0) {
    return (
      <div
        className={classNames(styles.gamePrizes, {
          [styles.holdemContainer]: isHoldemMax
        })}
      >
        {tableNoContent}
      </div>
    );
  }

  return (
    <div
      className={classNames(styles.gamePrizes, {
        [styles.holdemContainer]: isHoldemMax
      })}
    >
      {isHoldemMax ? (
        <>
          <div className={styles.holdemTableContainer} ref={prizeTableRef}>
            <HoldemMaxPrizeTable
              drawLevels={drawLevels}
              placeAwardsMaxLength={placeAwardsMaxLength}
              data={modLevels}
              scalePM={scalePM}
              currency={currency}
            />
          </div>
          <div className={styles.prizeInfo}>
            * <Translation tagName="span" {...TXTCLI_These_prize_values_are_average_ELL} />
          </div>
        </>
      ) : (
        <div className={sharedStyles.scrollableTable} ref={prizeTableRef}>
          <table className={styles.prizeTable}>
            {!!drawLevels.length && (
              <TableHead
                placeAwards={drawLevels[0].placeAwards}
                placeAwardsMaxLength={placeAwardsMaxLength}
              />
            )}
            <tbody data-testid="prizePool" className="prize-pool-table-body">
              {mappedDrawLevels.map(({ odds, awards }, i) => (
                <tr key={`${odds}-${i}`} className="prize-tool-table-body-row">
                  {awards &&
                    awards.map((award, index) => {
                      if (!award) {
                        return <td key={`no-${index}`} data-testid="empty" />;
                      }

                      return (
                        <Prize
                          key={`${odds}-${award}-${index}`}
                          value={scalePM && typeof award !== 'string' ? award * scalePM : award}
                          className={classNames({
                            [styles.gamePrizeGold]: index === 0,
                            [styles.gamePrizeSilver]: index === 1,
                            [styles.gamePrizeBronze]: index === 2
                          })}
                          currency={currency}
                          isCurrency
                        />
                      );
                    })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PrizePool;
